import type { CabinetDimensionAxis } from './cabinet-pieces-math';

/**
 * Spreadsheet-style dimensions for a cut-list row.
 *
 * A piece is usually the cabinet's own height, width or depth give or take a
 * board thickness — "the left side is the height minus 36". Typing the arithmetic
 * out once and letting it follow the cabinet is the difference between changing
 * one number and re-measuring a whole cut list, so a row may hold a formula
 * instead of a fixed millimetre value:
 *
 *     @H - 36        the cabinet's height, less 36 mm
 *     @W / 2 - 9     half the width, less a divider
 *     (@D - 18) / 2
 *
 * `@` then a letter names one of the cabinet's three dimensions. Both languages
 * are always accepted, so a formula written on a Romanian screen still reads on
 * an English one and vice versa:
 *
 *     height   @H (Height)    @I (Înălțime)
 *     width    @W (Width)     @L (Lățime)
 *     depth    @D (Depth)     @A (Adâncime)
 *
 * Evaluated by the hand-written parser below rather than by `eval` or `Function`:
 * these strings are user input that round-trips through the database, so they
 * must never be executable.
 */

export interface CabinetDimensionValues {
  heightMm: number;
  widthMm: number;
  depthMm: number;
}

/** The letter that follows `@`, per dimension and per language. */
export const CABINET_FORMULA_TOKENS: Readonly<Record<string, CabinetDimensionAxis>> = {
  H: 'HEIGHT',
  I: 'HEIGHT',
  Î: 'HEIGHT',
  W: 'WIDTH',
  L: 'WIDTH',
  D: 'DEPTH',
  A: 'DEPTH',
};

/** Every accepted token, for the editor's suggestion list. */
export const CABINET_FORMULA_TOKEN_ENTRIES: ReadonlyArray<{
  token: string;
  axis: CabinetDimensionAxis;
}> = Object.entries(CABINET_FORMULA_TOKENS).map(([token, axis]) => ({ token, axis }));

export type CabinetFormulaError =
  /** Not parseable at all — a stray operator, an unclosed bracket, junk. */
  | 'SYNTAX'
  /** `@` followed by a letter that names no dimension. */
  | 'UNKNOWN_TOKEN'
  /** Division by zero, or anything else that lands on NaN/Infinity. */
  | 'NOT_FINITE'
  /** Parsed and finite, but outside the 1..100000 mm a piece may be. */
  | 'OUT_OF_RANGE';

export type CabinetFormulaResult =
  | { ok: true; valueMm: number }
  | { ok: false; error: CabinetFormulaError };

/** The smallest and largest a cut-list dimension may be (mirrors `pieceDimMm`). */
export const MIN_PIECE_DIM_MM = 1;
export const MAX_PIECE_DIM_MM = 100_000;

/**
 * Whether this input is a formula rather than a plain measurement.
 *
 * Keyed on `@` alone: a dimension that references nothing has nothing to follow,
 * so it is stored as the number it is. That also keeps the check cheap enough to
 * run on every keystroke.
 */
export function isCabinetDimensionFormula(value: string): boolean {
  return value.includes('@');
}

// ---------------------------------------------------------------------------
// Recursive-descent parser.
//
//   expr    := term (('+' | '-') term)*
//   term    := factor (('*' | '/') factor)*
//   factor  := ('+' | '-')* primary
//   primary := number | '@' letter | '(' expr ')'
//
// Deliberately tiny: there is no reason a board dimension should need more than
// the four operators and brackets, and every construct left out is one that
// cannot then misbehave.
// ---------------------------------------------------------------------------

class FormulaSyntaxError extends Error {
  constructor(readonly kind: CabinetFormulaError) {
    super(kind);
  }
}

interface Cursor {
  readonly text: string;
  index: number;
}

const isDigit = (c: string) => c >= '0' && c <= '9';

function skipSpace(cur: Cursor): void {
  while (cur.index < cur.text.length && /\s/.test(cur.text[cur.index]!)) cur.index += 1;
}

function peek(cur: Cursor): string | undefined {
  skipSpace(cur);
  return cur.text[cur.index];
}

function parsePrimary(cur: Cursor, dims: CabinetDimensionValues): number {
  const c = peek(cur);
  if (c === undefined) throw new FormulaSyntaxError('SYNTAX');

  if (c === '(') {
    cur.index += 1;
    const value = parseExpr(cur, dims);
    if (peek(cur) !== ')') throw new FormulaSyntaxError('SYNTAX');
    cur.index += 1;
    return value;
  }

  if (c === '@') {
    cur.index += 1;
    const letter = cur.text[cur.index];
    if (letter === undefined) throw new FormulaSyntaxError('UNKNOWN_TOKEN');
    cur.index += 1;
    // Upper-cased so `@h` and `@H` are the same token; the map holds both the
    // English and the Romanian letter for each dimension.
    const axis = CABINET_FORMULA_TOKENS[letter.toUpperCase()];
    if (!axis) throw new FormulaSyntaxError('UNKNOWN_TOKEN');
    return axis === 'HEIGHT' ? dims.heightMm : axis === 'WIDTH' ? dims.widthMm : dims.depthMm;
  }

  if (isDigit(c) || c === '.') {
    const start = cur.index;
    while (cur.index < cur.text.length) {
      const ch = cur.text[cur.index]!;
      if (!isDigit(ch) && ch !== '.') break;
      cur.index += 1;
    }
    const value = Number(cur.text.slice(start, cur.index));
    if (!Number.isFinite(value)) throw new FormulaSyntaxError('SYNTAX');
    return value;
  }

  throw new FormulaSyntaxError('SYNTAX');
}

function parseFactor(cur: Cursor, dims: CabinetDimensionValues): number {
  const c = peek(cur);
  if (c === '-') {
    cur.index += 1;
    return -parseFactor(cur, dims);
  }
  if (c === '+') {
    cur.index += 1;
    return parseFactor(cur, dims);
  }
  return parsePrimary(cur, dims);
}

function parseTerm(cur: Cursor, dims: CabinetDimensionValues): number {
  let value = parseFactor(cur, dims);
  for (;;) {
    const c = peek(cur);
    if (c !== '*' && c !== '/') return value;
    cur.index += 1;
    const rhs = parseFactor(cur, dims);
    value = c === '*' ? value * rhs : value / rhs;
  }
}

function parseExpr(cur: Cursor, dims: CabinetDimensionValues): number {
  let value = parseTerm(cur, dims);
  for (;;) {
    const c = peek(cur);
    if (c !== '+' && c !== '-') return value;
    cur.index += 1;
    const rhs = parseTerm(cur, dims);
    value = c === '+' ? value + rhs : value - rhs;
  }
}

/**
 * Work out what a formula comes to against a cabinet's dimensions.
 *
 * Rounds to whole millimetres, because that is what a piece is stored in — so
 * `@W / 2` on a 601 mm cabinet is 301, not 300.5.
 *
 * Never throws: a formula is typed a character at a time, and "@H -" is simply
 * not finished yet rather than an exception the caller has to catch.
 */
export function evaluateCabinetDimensionFormula(
  formula: string,
  dims: CabinetDimensionValues,
): CabinetFormulaResult {
  const cur: Cursor = { text: formula, index: 0 };
  let raw: number;
  try {
    raw = parseExpr(cur, dims);
    skipSpace(cur);
    // Trailing junk ("@H 36") is a mistake, not a value.
    if (cur.index !== cur.text.length) throw new FormulaSyntaxError('SYNTAX');
  } catch (error) {
    return {
      ok: false,
      error: error instanceof FormulaSyntaxError ? error.kind : 'SYNTAX',
    };
  }

  if (!Number.isFinite(raw)) return { ok: false, error: 'NOT_FINITE' };
  const valueMm = Math.round(raw);
  if (valueMm < MIN_PIECE_DIM_MM || valueMm > MAX_PIECE_DIM_MM) {
    return { ok: false, error: 'OUT_OF_RANGE' };
  }
  return { ok: true, valueMm };
}

/**
 * The millimetres a row should carry, given what the user typed.
 *
 * The single place that decides "is this a formula, and if so what does it come
 * to" — used by the editor as you type, and again on the server when a cabinet
 * is saved, so a stored dimension always agrees with the formula beside it.
 * A formula that does not (yet) evaluate leaves the previous value alone rather
 * than blanking the row.
 */
export function resolveCabinetDimension(
  input: string,
  dims: CabinetDimensionValues,
  fallbackMm: number,
): { valueMm: number; formula: string | null; error: CabinetFormulaError | null } {
  if (!isCabinetDimensionFormula(input)) {
    const parsed = Number(input);
    return {
      valueMm: Number.isFinite(parsed) ? Math.round(parsed) : fallbackMm,
      formula: null,
      error: null,
    };
  }
  const result = evaluateCabinetDimensionFormula(input, dims);
  return result.ok
    ? { valueMm: result.valueMm, formula: input, error: null }
    : { valueMm: fallbackMm, formula: input, error: result.error };
}
